import { useEffect, useState } from "react";
import { Download, ScrollText, ClipboardList } from "lucide-react";
import { staffApi, getToken, API_BASE } from "../lib/api";

export default function ReportsPage() {
  const [audit, setAudit] = useState(null);
  const [auditError, setAuditError] = useState("");
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    staffApi.get("/staff/audit-log").then((r) => setAudit(r.data.entries)).catch((e) => setAuditError(e?.response?.data?.detail || ""));
    staffApi.get("/staff/progress").then((r) => setProgress(r.data)).catch(() => setProgress({ choristers: [] }));
  }, []);

  const exportCsv = async () => {
    const token = getToken("staff");
    const res = await fetch(`${API_BASE}/staff/reports/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include", // httpOnly cookie carries the session when no in-memory token
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "choirquest-report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">Reports</h1>
        <button onClick={exportCsv} data-testid="export-csv-btn"
          className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm hover:bg-[#12253E] transition-colors min-h-[48px]">
          <Download className="w-4 h-4" /> Export chorister report (CSV)
        </button>
      </div>

      <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="progress-section">
        <h2 className="font-display text-lg font-bold text-[#0B192C] flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-[#C69B3D]" /> Tasks & deadlines
        </h2>
        <p className="text-xs text-[#566270] mt-1">How each chorister is getting on with the tasks you've set from Our Content.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]" data-testid="progress-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#566270] border-b border-[#E5DEC9]">
                <th className="py-2 pr-4">Chorister</th><th className="py-2 pr-4">Groups</th>
                <th className="py-2 pr-4">Done</th><th className="py-2 pr-4">Overdue</th><th className="py-2 pr-4">Due soon</th>
                <th className="py-2 pr-4">Last completed</th>
              </tr>
            </thead>
            <tbody>
              {(progress?.choristers || []).map((c) => (
                <tr key={c.chorister_id} className="border-b border-[#F3EFE0]" data-testid={`progress-row-${c.chorister_id}`}>
                  <td className="py-2.5 pr-4 font-semibold text-[#0B192C]">{c.display_name}</td>
                  <td className="py-2.5 pr-4 text-[#566270]">{(c.groups || []).join(", ") || "—"}</td>
                  <td className="py-2.5 pr-4 text-[#1C242B]">{c.tasks_done}/{c.tasks_assigned}</td>
                  <td className={`py-2.5 pr-4 font-bold ${c.tasks_overdue ? "text-[#A62B2B]" : "text-[#566270]"}`}>{c.tasks_overdue || "—"}</td>
                  <td className={`py-2.5 pr-4 font-bold ${c.tasks_due_soon ? "text-[#B8860B]" : "text-[#566270]"}`}>{c.tasks_due_soon || "—"}</td>
                  <td className="py-2.5 pr-4 text-[#566270]">{c.last_completed_at ? new Date(c.last_completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}</td>
                </tr>
              ))}
              {progress && progress.choristers.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-[#566270]">No active choristers yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="audit-log-section">
        <h2 className="font-display text-lg font-bold text-[#0B192C] flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-[#C69B3D]" /> Audit log
        </h2>
        <p className="text-xs text-[#566270] mt-1">Important staff and admin actions are recorded here.</p>
        {auditError && <p className="text-sm text-[#566270] mt-3">{auditError}</p>}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#566270] border-b border-[#E5DEC9]">
                <th className="py-2 pr-4">When</th><th className="py-2 pr-4">Who</th><th className="py-2 pr-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {(audit || []).map((a) => (
                <tr key={a.id} className="border-b border-[#F3EFE0]">
                  <td className="py-2.5 pr-4 text-[#566270] whitespace-nowrap">{new Date(a.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="py-2.5 pr-4 font-semibold text-[#0B192C]">{a.staff_name}</td>
                  <td className="py-2.5 text-[#1C242B]">{a.action.replace(/_/g, " ")}</td>
                </tr>
              ))}
              {audit && audit.length === 0 && (
                <tr><td colSpan={3} className="py-6 text-center text-[#566270]">No actions recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
